import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast, Toaster } from 'sonner';
import { Eye, EyeOff, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { PhoneInput } from '@/components/ui/phone-input';
import { useBranchTheme } from '@/lib/branchTheme';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// InvictaFit-Verlauf (Orange → Rot-Orange) für Buttons/Akzente — identisch zu LoginPage
const BRAND_GRADIENT = 'linear-gradient(135deg, #22C55E 0%, #0F766E 100%)';

const DEFAULT_FEATURES = [
  'Online-Buchung & Kursplanung',
  'Kunden, Termine & Zahlungen an einem Ort',
  'Trainings- & Ernährungspläne mit KI',
  'Eigene App mit Ihrem Logo',
];
const DEFAULT_SUBTITLE =
  'Die Plattform für Studios, Praxen und Gesundheitsbetriebe — Buchung, Kundenverwaltung und Zahlungen an einem Ort.';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { logoUrl, name: brandName } = useTenantBranding();
  const { branch } = useBranchTheme();

  // Branchen-Theme (Logo, Text, Farben) – Fallback: InvictaFit-Branding.
  const accent = branch?.accent_color || '#16A34A';
  const GRAD = branch?.accent_gradient || BRAND_GRADIENT;
  const panelLogo = branch ? branch.logo_url : logoUrl;
  const panelName = branch?.name || brandName;
  const panelSubtitle = branch?.subtitle || branch?.description || DEFAULT_SUBTITLE;
  const panelFeatures = (branch?.features || DEFAULT_FEATURES).slice(0, 4);
  const branchQuery = branch ? `?branch=${branch.key}` : '';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'customer',
    phone: '',
    gender: '', // Pflichtfeld
    referral_source_id: '', // Optional - Wie haben Sie von uns erfahren?
    accepted_terms: false
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [referralSources, setReferralSources] = useState([]);

  // Lade Marketing-Quellen beim Start
  useEffect(() => {
    const loadReferralSources = async () => {
      try {
        const response = await axios.get(`${API}/referral-sources/public`);
        setReferralSources(response.data || []);
      } catch (error) {
        console.log('Referral sources not available');
      }
    };
    loadReferralSources();
  }, []);

  const loadTerms = async () => {
    try {
      const response = await axios.get(`${API}/policies/terms`);
      setTermsContent(response.data.content || 'AGB nicht verfügbar');
      setShowTerms(true);
    } catch (error) {
      setTermsContent('AGB konnten nicht geladen werden');
      setShowTerms(true);
    }
  };

  const loadPrivacy = async () => {
    try {
      const response = await axios.get(`${API}/policies/privacy`);
      setPrivacyContent(response.data.content || 'Datenschutzerklärung nicht verfügbar');
      setShowPrivacy(true);
    } catch (error) {
      setPrivacyContent('Datenschutzerklärung konnte nicht geladen werden');
      setShowPrivacy(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.accepted_terms) {
      toast.error('Bitte akzeptieren Sie die AGB und Datenschutzerklärung');
      return;
    }

    if (!formData.phone || formData.phone.trim().length < 5) {
      toast.error('Bitte geben Sie eine gültige Telefonnummer ein');
      return;
    }

    if (!formData.gender) {
      toast.error('Bitte wählen Sie Ihr Geschlecht aus');
      return;
    }

    setLoading(true);

    try {
      await register({
        ...formData,
        agb_accepted: true,
        accepted_terms_at: new Date().toISOString(),
        signature_data: null
      });

      toast.success('Registrierung erfolgreich! Sie können nun Termine buchen.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="register-page">
      <Toaster position="top-center" richColors />

      {/* Left Side - Branchen-Branding (hell, identisch zu LoginPage) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}12 0%, #FFFFFF 55%, #F8FAFC 100%)` }}
      >
        {/* Branchen-Glow */}
        <div
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-40"
          style={{ background: `radial-gradient(circle, ${accent}2e, transparent 70%)` }}
        />
        <div
          className="absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full opacity-30"
          style={{ background: `radial-gradient(circle, ${accent}24, transparent 70%)` }}
        />

        <div className="relative z-10 max-w-md animate-fade-up">
          <img
            src={panelLogo}
            alt={panelName}
            data-testid="register-brand-logo"
            className="w-44 h-44 object-contain mb-6 drop-shadow-sm"
          />
          <h2
            className="text-3xl sm:text-4xl font-bold leading-tight text-slate-900"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Weniger Büroarbeit.<br />
            <span style={{ color: accent }}>Mehr Zeit für Ihre Kunden.</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {panelSubtitle}
          </p>
          <div className="mt-10 space-y-4 stagger-children">
            {panelFeatures.map((text, i) => (
              <div key={i} className="flex items-center gap-4 group">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center border transition-colors"
                  style={{ borderColor: `${accent}40`, backgroundColor: `${accent}12` }}
                >
                  <ChevronRight size={18} style={{ color: accent }} />
                </div>
                <span className="text-slate-700 text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-slate-50">
        {/* Mobile Logo */}
        <div className="lg:hidden absolute top-6 left-4 flex items-center gap-2">
          <img src={panelLogo} alt={panelName} className="h-10 w-auto object-contain" />
        </div>

        <Card className="w-full max-w-md mt-16 lg:mt-0 border-slate-200/70 shadow-xl animate-fade-up" data-testid="register-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl sm:text-3xl text-slate-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              REGISTRIEREN
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Erstellen Sie Ihr Konto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vorname *</Label>
                  <Input
                    id="first_name"
                    data-testid="register-firstname-input"
                    placeholder="Max"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nachname *</Label>
                  <Input
                    id="last_name"
                    data-testid="register-lastname-input"
                    placeholder="Mustermann"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-Mail *</Label>
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="name@beispiel.ch"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Telefon *</Label>
                <PhoneInput
                  id="phone"
                  data-testid="register-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  isDark={false}
                />
              </div>

              {/* Geschlecht Auswahl */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Geschlecht *</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    data-testid="register-gender-male"
                    onClick={() => setFormData({ ...formData, gender: 'male' })}
                    style={formData.gender === 'male' ? { borderColor: accent, backgroundColor: `${accent}14`, color: accent } : undefined}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      formData.gender === 'male'
                        ? ''
                        : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    ♂ Mann
                  </button>
                  <button
                    type="button"
                    data-testid="register-gender-female"
                    onClick={() => setFormData({ ...formData, gender: 'female' })}
                    style={formData.gender === 'female' ? { borderColor: accent, backgroundColor: `${accent}14`, color: accent } : undefined}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      formData.gender === 'female'
                        ? ''
                        : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    ♀ Frau
                  </button>
                </div>
              </div>

              {/* Wie haben Sie von uns erfahren? (Optional) */}
              {referralSources.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Wie haben Sie von uns erfahren?</Label>
                  <Select
                    value={formData.referral_source_id}
                    onValueChange={(value) => setFormData({ ...formData, referral_source_id: value })}
                  >
                    <SelectTrigger
                      data-testid="register-referral-source"
                      className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200"
                    >
                      <SelectValue placeholder="Bitte auswählen (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {referralSources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.icon} {source.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Passwort *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mindestens 6 Zeichen"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    className="h-11 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* AGB & Datenschutz Checkbox */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <input
                  type="checkbox"
                  id="accepted_terms"
                  data-testid="terms-checkbox"
                  checked={formData.accepted_terms}
                  onChange={(e) => setFormData({ ...formData, accepted_terms: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded"
                  style={{ accentColor: accent }}
                />
                <label htmlFor="accepted_terms" className="text-sm text-slate-600">
                  Ich akzeptiere die{' '}
                  <button
                    type="button"
                    onClick={loadTerms}
                    className="underline font-medium hover:opacity-80"
                    style={{ color: accent }}
                  >
                    AGB
                  </button>
                  {' '}und die{' '}
                  <button
                    type="button"
                    onClick={loadPrivacy}
                    className="underline font-medium hover:opacity-80"
                    style={{ color: accent }}
                  >
                    Datenschutzerklärung
                  </button>
                  {' '}*
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-lg font-bold uppercase tracking-wide text-white transition-all active:scale-[0.97]"
                disabled={loading || !formData.accepted_terms}
                data-testid="register-submit-button"
                style={{ background: GRAD }}
              >
                {loading ? 'Wird registriert...' : 'Registrieren'}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-slate-500">Bereits ein Konto? </span>
              <Link
                to={`/login${branchQuery}`}
                className="font-semibold hover:opacity-80"
                style={{ color: accent }}
                data-testid="login-link"
              >
                Jetzt anmelden
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AGB Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Allgemeine Geschäftsbedingungen (AGB)</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-slate-600">
              {termsContent}
            </pre>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={() => setShowTerms(false)}
              className="text-white"
              style={{ background: GRAD }}
            >
              Schliessen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Datenschutz Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Datenschutzerklärung</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed text-slate-600">
              {privacyContent}
            </pre>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={() => setShowPrivacy(false)}
              className="text-white"
              style={{ background: GRAD }}
            >
              Schliessen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterPage;
